/**
 * `RESTORE_DAILY_VERSION` step. Single-call wrapper over
 * `POST /api/v2/route/{routeId}/daily/stops/restore`. The BE keeps daily
 * extras (not in the version) at the top in existing order, then lays the
 * version stops underneath in stored order. With `reassign:true` it first
 * re-attaches version stops that have been unassigned since.
 */
import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { StepResult } from '../types'
import type { RouteId } from 'types/entities/shared/ids'
import type { Deps, PipelineStep } from 'types/modules/routing'

export interface RestoreDailyVersionStepArgs {
  readonly routeId: RouteId
  readonly versionId: string
  readonly reassign: boolean
}

export type RestoreDailyVersionStepValue = { readonly stopCount: number }

export type RestoreDailyVersionStepResult = StepResult<'RESTORE_DAILY_VERSION', RestoreDailyVersionStepValue>

export const makeRestoreDailyVersionStep = (
  args: RestoreDailyVersionStepArgs,
  deps: Deps
): PipelineStep<'RESTORE_DAILY_VERSION', RestoreDailyVersionStepResult> => {
  const stepId = `restore-daily-version:${args.routeId}:${args.versionId}`
  return {
    id: stepId,
    phase: 'RESTORE_DAILY_VERSION',
    label: 'Failed to restore daily sequence version',
    applyOrder: 200,
    run: async () => {
      try {
        const response = await deps
          .restoreDailyVersion({
            routeId: args.routeId,
            data: {
              versionId: args.versionId,
              reassign: args.reassign,
            },
            skipInvalidate: true,
          })
          .unwrap()
        return ok({
          phase: 'RESTORE_DAILY_VERSION',
          stepId,
          value: { stopCount: response?.stopCount ?? 0 },
        })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({
          kind: 'BE_REJECTED',
          phase: 'RESTORE_DAILY_VERSION',
          status,
          message,
        })
      }
    },
  }
}
